import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ImagePlus, Loader2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { useGenerateAnnouncementDraftMutation } from '@/store/announcementsApi';
import { Announcement } from '@/types/Announcement';

interface Props {
    open: boolean;
    onOpenChange: (v: boolean) => void;
    onGenerated: (draft: Pick<Announcement, 'type' | 'desc' | 'long'>) => void;
}

const EXAMPLE_PROMPT = 'צור לי הודעה עם כותרת, מסוג "מה חדש", כדי לעדכן את הלקוחות בשינויים שלי.\n'
    + 'קח את השינויים מהיום ואתמול, ותתאר בהרחבה את השינויים בצילום המסך המצורף';

interface PastedScreenshot {
    dataUrl: string;
    base64: string;
    mime: string;
}

export function AnnouncementAiGenerateModal({ open, onOpenChange, onGenerated }: Props) {
    const [prompt, setPrompt] = useState('');
    const [screenshot, setScreenshot] = useState<PastedScreenshot | null>(null);
    const [generateDraft, { isLoading }] = useGenerateAnnouncementDraftMutation();

    useEffect(() => {
        if (open) {
            setPrompt('');
            setScreenshot(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        function handlePaste(event: ClipboardEvent) {
            const clipboardItems = event.clipboardData?.items;
            if (!clipboardItems) {
                return;
            }
            for (let i = 0; i < clipboardItems.length; i++) {
                const item = clipboardItems[i];
                if (item.type.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (!file) {
                        continue;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                        const dataUrl = reader.result as string;
                        const base64 = dataUrl.split(',')[1] ?? '';
                        setScreenshot({ dataUrl, base64, mime: file.type });
                        toast.success('צילום המסך צורף');
                    };
                    reader.readAsDataURL(file);
                    return;
                }
            }
        }

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [open]);

    async function handleSubmit() {
        if (!prompt.trim()) {
            toast.error('יש להזין פרומפט');
            return;
        }
        try {
            const draft = await generateDraft({
                prompt,
                screenshot_base64: screenshot?.base64,
                screenshot_mime: screenshot?.mime,
            }).unwrap();
            toast.success('הטיוטה נוצרה בהצלחה, ניתן לערוך ולשמור');
            onGenerated(draft);
            onOpenChange(false);
        } catch (err: unknown) {
            const msg = (err as { data?: { detail?: string } })?.data?.detail ?? 'שגיאה ביצירת הטיוטה';
            toast.error(msg);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl p-6">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-amber-500" />
                        יצירת הודעה באמצעות AI
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <div className="space-y-1">
                        <Label>פרומפט</Label>
                        <Textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={5}
                            placeholder={EXAMPLE_PROMPT}
                        />
                        <p className="text-xs text-gray-400">
                            המערכת תצרף אוטומטית תמצית של השינויים האחרונים בגיט (יום-יומיים אחרונים).
                        </p>
                    </div>

                    <div className="space-y-1">
                        <Label>צילום מסך (אופציונלי)</Label>
                        {screenshot ? (
                            <div className="relative inline-block">
                                <img src={screenshot.dataUrl} alt="צילום מסך" className="max-h-40 rounded-md border border-gray-200" />
                                <button
                                    type="button"
                                    onClick={() => setScreenshot(null)}
                                    className="absolute -top-2 -right-2 rounded-full bg-white border border-gray-200 shadow p-0.5 text-gray-500 hover:text-red-500"
                                    aria-label="הסר תמונה"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 p-6 text-sm text-gray-400">
                                <ImagePlus className="h-4 w-4" />
                                הדבק צילום מסך (Ctrl+V) בכל מקום בחלון הזה
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>ביטול</Button>
                    <Button type="button" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? <Loader2 className="h-4 w-4 ml-1 animate-spin" /> : <Sparkles className="h-4 w-4 ml-1" />}
                        {isLoading ? 'יוצר טיוטה...' : 'צור טיוטה'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
